import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import type { FhirIntegrationViewModel } from '../integrationApi';

export function DeleteFhirIntegrationConfirmDialog({
  row,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly row: FhirIntegrationViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (row: FhirIntegrationViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('routes.admin.fhirIntegrations.entries.delete.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('routes.admin.fhirIntegrations.entries.delete.message', { name: row?.name ?? '' })}
        </p>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.fhirIntegrations.entries.form.errors.submit', {
              code: getErrorCodeForUi(submitError),
            })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.admin.fhirIntegrations.entries.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (row !== null) onConfirm(row);
            }}
          >
            {t('routes.admin.fhirIntegrations.entries.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
